import FontAwesomeIcon from "~/components/FontAwesome";
import {Link} from "react-router-dom";
import {useCan} from "~/hooks";

/**
 * Danh sách menu điều hướng.
 *
 * Skeleton khởi tạo dự án mới: chỉ còn Dashboard + Hồ sơ cá nhân. Module nghiệp vụ
 * mới tự thêm mục (kèm kiểm tra quyền qua useCan) vào danh sách này.
 *
 * NavBarData được gọi trong render của Sidebar nên dùng hook useCan ở đây hợp lệ.
 */
export const NavBarData = () => {

	const canSeo = useCan('seo');
	const canPageSpeed = useCan('pagespeed');
	const canSchema = useCan('schema');
	const canProject = useCan('project');
	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canAi = useCan('ai');
	const canMember = useCan('member_manage');
	const canPermission = useCan('permission');

	const items = [
		{
			key: 'home',
			label: <Link to="/">Dashboard</Link>,
			title: 'Dashboard',
			to: '/',
			icon: <FontAwesomeIcon icon="fa-light fa-house" />,
			count: 0,
		},
	];

	// Chấm điểm SEO (cần quyền 'seo'; root bypass).
	if (canSeo) {
		items.push({
			key: 'seo',
			label: 'Chấm điểm SEO',
			title: 'Chấm điểm SEO',
			to: '/seo',
			icon: <FontAwesomeIcon icon="fa-light fa-magnifying-glass-chart" />,
			children: [
				{
					key: 'seo-check',
					label: <Link to="/seo">Chấm điểm mới</Link>,
					title: 'Chấm điểm mới',
					to: '/seo',
				},
				{
					key: 'seo-history',
					label: <Link to="/seo/history">Lịch sử</Link>,
					title: 'Lịch sử',
					to: '/seo/history',
				},
			],
		});
	}

	// PageSpeed / Core Web Vitals (cần quyền 'pagespeed'; root bypass).
	if (canPageSpeed) {
		items.push({
			key: 'pagespeed',
			label: 'PageSpeed / CWV',
			title: 'PageSpeed / Core Web Vitals',
			to: '/pagespeed',
			icon: <FontAwesomeIcon icon="fa-light fa-gauge-high" />,
			children: [
				{
					key: 'pagespeed-check',
					label: <Link to="/pagespeed">Kiểm tra mới</Link>,
					title: 'Kiểm tra mới',
					to: '/pagespeed',
				},
				{
					key: 'pagespeed-history',
					label: <Link to="/pagespeed/history">Lịch sử</Link>,
					title: 'Lịch sử',
					to: '/pagespeed/history',
				},
			],
		});
	}

	// Schema.org Generator + Validator (cần quyền 'schema'; root bypass).
	if (canSchema) {
		items.push({
			key: 'schema',
			label: 'Schema.org',
			title: 'Schema.org Generator/Validator',
			to: '/schema/generator',
			icon: <FontAwesomeIcon icon="fa-light fa-shapes" />,
			children: [
				{
					key: 'schema-generator',
					label: <Link to="/schema/generator">Tạo schema</Link>,
					title: 'Tạo schema',
					to: '/schema/generator',
				},
				{
					key: 'schema-validator',
					label: <Link to="/schema/validator">Kiểm tra schema</Link>,
					title: 'Kiểm tra schema',
					to: '/schema/validator',
				},
				{
					key: 'schema-saved',
					label: <Link to="/schema/saved">Đã lưu</Link>,
					title: 'Đã lưu',
					to: '/schema/saved',
				},
			],
		});
	}

	// AI SEO (cần quyền 'ai'; root bypass).
	if (canAi) {
		items.push({
			key: 'ai',
			label: 'AI SEO',
			title: 'AI SEO — sinh nội dung',
			to: '/ai',
			icon: <FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />,
			children: [
				{
					key: 'ai-create',
					label: <Link to="/ai">Tạo nội dung</Link>,
					title: 'Tạo nội dung',
					to: '/ai',
				},
				{
					key: 'ai-history',
					label: <Link to="/ai/history">Lịch sử</Link>,
					title: 'Lịch sử',
					to: '/ai/history',
				},
			],
		});
	}

	// Dự án (thực thể tổng chứa các tool SEO). Hiện khi có quyền quản lý dự án
	// hoặc có quyền bất kỳ tool nào bên trong (vd Site Crawler). root bypass.
	if (canProject || canCrawl || canGsc || canAi) {
		items.push({
			key: 'projects',
			label: <Link to="/projects">Dự án</Link>,
			title: 'Dự án',
			to: '/projects',
			icon: <FontAwesomeIcon icon="fa-light fa-folder-tree" />,
			count: 0,
		});
	}

	// Quản trị: thành viên & gói (cap 'member_manage') + phân quyền (cap 'permission'); root bypass.
	if (canMember || canPermission) {
		const adminChildren = [];

		if (canMember) {
			adminChildren.push(
				{
					key: 'admin-stats',
					label: <Link to="/admin/stats">Thống kê tổng quan</Link>,
					title: 'Thống kê tổng quan',
					to: '/admin/stats',
				},
				{
					key: 'admin-members',
					label: <Link to="/admin/members">Thành viên</Link>,
					title: 'Thành viên',
					to: '/admin/members',
				},
				{
					key: 'admin-plans',
					label: <Link to="/admin/plans">Gói dịch vụ</Link>,
					title: 'Gói dịch vụ',
					to: '/admin/plans',
				},
			);
		}

		if (canPermission) {
			adminChildren.push({
				key: 'admin-permission',
				label: <Link to="/permission">Phân quyền</Link>,
				title: 'Phân quyền',
				to: '/permission',
			});
		}

		items.push({
			key: 'admin',
			label: 'Quản trị',
			title: 'Quản trị hệ thống',
			to: canMember ? '/admin/members' : '/permission',
			icon: <FontAwesomeIcon icon="fa-light fa-users-gear" />,
			children: adminChildren,
		});
	}

	// Gói & Hạn mức — mọi user đều xem được gói của chính mình (không cần quyền riêng).
	items.push({
		key: 'subscription',
		label: <Link to="/subscription">Gói & Hạn mức</Link>,
		title: 'Gói & Hạn mức',
		to: '/subscription',
		icon: <FontAwesomeIcon icon="fa-light fa-crown" />,
		count: 0,
	});

	items.push({
		key: 'profile',
		label: <Link to="/profile">Hồ sơ cá nhân</Link>,
		title: 'Hồ sơ cá nhân',
		to: '/profile',
		icon: <FontAwesomeIcon icon="fa-light fa-id-card" />,
		count: 0,
	});

	return items;
}
